import { registerDocumentImages, unlinkImageFromDocument } from '@/lib/zybooks-api';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { Editor } from '@tiptap/react';

const MA_TOOL_NODE_TYPES = new Set(['zyAnimator', 'progressionBuilder', 'zyTool']);

export type ToolImageLinkedMap = Record<string, string>;

interface MaDocumentContext {
    documentId: number;
    subjectName: string;
    releaseId: string | number;
}

const pendingAddsByEditor = new WeakMap<Editor, Set<string>>();
const inFlightSyncByEditor = new WeakMap<Editor, Map<string, Promise<ToolImageLinkedMap>>>();
const linkedByToolNodeIdByEditor = new WeakMap<Editor, Map<string, ToolImageLinkedMap>>();

function getLinkedByToolNodeIdStore(editor: Editor): Map<string, ToolImageLinkedMap> {
    const existing = linkedByToolNodeIdByEditor.get(editor);
    if (existing) {
        return existing;
    }
    const linkedByToolNodeId = new Map<string, ToolImageLinkedMap>();
    linkedByToolNodeIdByEditor.set(editor, linkedByToolNodeId);
    return linkedByToolNodeId;
}

function getEffectiveLinkedMap(editor: Editor, toolNodeId: string, attrsLinkedMap: ToolImageLinkedMap): ToolImageLinkedMap {
    const fromMemory = getLinkedByToolNodeIdStore(editor).get(toolNodeId) ?? {};
    return { ...attrsLinkedMap, ...fromMemory };
}

function setMemoryLinkedMap(editor: Editor, toolNodeId: string, linkedMap: ToolImageLinkedMap): void {
    getLinkedByToolNodeIdStore(editor).set(toolNodeId, linkedMap);
}

function clearMemoryLinkedMap(editor: Editor, toolNodeId: string): void {
    getLinkedByToolNodeIdStore(editor).delete(toolNodeId);
}

export function hasUnlinkedImageIds(editor: Editor, toolNodeId: string, imageIds: string[], attrsLinkedMap: ToolImageLinkedMap): boolean {
    const effectiveLinkedMap = getEffectiveLinkedMap(editor, toolNodeId, attrsLinkedMap);
    const pendingAdds = getPendingAdds(editor);
    return imageIds.some(imageId => !effectiveLinkedMap[imageId] && !pendingAdds.has(getPendingKey(toolNodeId, imageId)));
}

function markPendingAddsForUnlinked(editor: Editor, toolNodeId: string, imageIds: string[], attrsLinkedMap: ToolImageLinkedMap): void {
    const effectiveLinkedMap = getEffectiveLinkedMap(editor, toolNodeId, attrsLinkedMap);
    const pendingAdds = getPendingAdds(editor);
    for (const imageId of imageIds) {
        if (!effectiveLinkedMap[imageId]) {
            pendingAdds.add(getPendingKey(toolNodeId, imageId));
        }
    }
}

function getInFlightSyncMap(editor: Editor): Map<string, Promise<ToolImageLinkedMap>> {
    const existing = inFlightSyncByEditor.get(editor);
    if (existing) {
        return existing;
    }
    const inFlight = new Map<string, Promise<ToolImageLinkedMap>>();
    inFlightSyncByEditor.set(editor, inFlight);
    return inFlight;
}

function getPendingAdds(editor: Editor): Set<string> {
    const existing = pendingAddsByEditor.get(editor);
    if (existing) {
        return existing;
    }
    const pending = new Set<string>();
    pendingAddsByEditor.set(editor, pending);
    return pending;
}

function getPendingKey(toolNodeId: string, imageId: string): string {
    return `${toolNodeId}:${imageId}`;
}

export function normalizeImageId(value: unknown): string {
    return (
        String(value ?? '')
            .split('?', 1)[0]
            .replace(/\/+$/, '')
            .split('/')
            .pop() ?? ''
    );
}

export function normalizeImageIds(imageIds: unknown): string[] {
    if (!Array.isArray(imageIds)) {
        return [];
    }
    return [...new Set(imageIds.map(normalizeImageId).filter(Boolean))];
}

export function getMaImageNodeIdsFromAttrs(attrs: Record<string, unknown>): ToolImageLinkedMap {
    const value = attrs.maImageNodeIds;
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return {};
    }
    const normalized: ToolImageLinkedMap = {};
    for (const [rawImageId, maImageNodeUuid] of Object.entries(value)) {
        const imageId = normalizeImageId(rawImageId);
        if (imageId && typeof maImageNodeUuid === 'string' && maImageNodeUuid !== '') {
            normalized[imageId] = maImageNodeUuid;
        }
    }
    return normalized;
}

function imageIdSetsEqual(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
        return false;
    }
    return left.every((imageId, index) => imageId === right[index]);
}

function buildPersistedMaImageNodeIds(desiredIds: string[], linkedMap: ToolImageLinkedMap): ToolImageLinkedMap {
    return Object.fromEntries(desiredIds.filter(imageId => linkedMap[imageId]).map(imageId => [imageId, linkedMap[imageId]]));
}

function getMaDocumentContext(editor: Editor): MaDocumentContext | null {
    const { documentId, subjectName, releaseId } = editor.state.doc.attrs;
    if (typeof documentId !== 'number' || documentId <= 0 || typeof subjectName !== 'string' || subjectName === '' || releaseId == null) {
        return null;
    }
    return { documentId, subjectName, releaseId };
}

export function findToolNodeAttrsById(editor: Editor, toolNodeId: string): Record<string, unknown> | null {
    let attrs: Record<string, unknown> | null = null;
    editor.state.doc.descendants(node => {
        if (MA_TOOL_NODE_TYPES.has(node.type.name) && String(node.attrs?.id) === toolNodeId) {
            attrs = node.attrs as Record<string, unknown>;
            return false;
        }
    });
    return attrs;
}

function persistMaImageNodeIdsOnEditor(editor: Editor, toolNodeId: string, maImageNodeIds: ToolImageLinkedMap): void {
    editor.state.doc.descendants((node, pos) => {
        if (!MA_TOOL_NODE_TYPES.has(node.type.name) || String(node.attrs?.id) !== toolNodeId) {
            return;
        }
        const current = getMaImageNodeIdsFromAttrs(node.attrs as Record<string, unknown>);
        const currentKeys = Object.keys(current).sort();
        const nextKeys = Object.keys(maImageNodeIds).sort();
        if (
            currentKeys.length === nextKeys.length &&
            currentKeys.every((key, index) => key === nextKeys[index] && current[key] === maImageNodeIds[key])
        ) {
            return false;
        }
        editor.view.dispatch(
            editor.state.tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                maImageNodeIds,
            }),
        );
        return false;
    });
}

export async function syncToolBuilderImages(
    editor: Editor,
    options: {
        toolNodeId: string;
        nextImageIds: string[];
        currentMaImageNodeIds: ToolImageLinkedMap;
    },
): Promise<ToolImageLinkedMap> {
    const context = getMaDocumentContext(editor);
    if (!context) {
        return buildPersistedMaImageNodeIds(options.nextImageIds, options.currentMaImageNodeIds);
    }

    const { toolNodeId, nextImageIds, currentMaImageNodeIds } = options;
    const linkedMap = { ...getEffectiveLinkedMap(editor, toolNodeId, currentMaImageNodeIds) };
    const pendingAdds = getPendingAdds(editor);

    for (const imageId of Object.keys(linkedMap)) {
        if (!nextImageIds.includes(imageId)) {
            const maImageNodeUuid = linkedMap[imageId];
            if (maImageNodeUuid) {
                await unlinkImageFromDocument(maImageNodeUuid, context.documentId).catch(error => {
                    console.error('Failed to unlink MA tool image from document:', error);
                });
            }
            delete linkedMap[imageId];
            pendingAdds.delete(getPendingKey(toolNodeId, imageId));
        }
    }

    const addedIds = nextImageIds.filter(imageId => !linkedMap[imageId]);

    if (addedIds.length === 0) {
        const persisted = buildPersistedMaImageNodeIds(nextImageIds, linkedMap);
        setMemoryLinkedMap(editor, toolNodeId, persisted);
        return persisted;
    }

    for (const imageId of addedIds) {
        pendingAdds.add(getPendingKey(toolNodeId, imageId));
    }

    try {
        const registeredImages = await registerDocumentImages(
            context.documentId,
            context.subjectName,
            context.releaseId,
            addedIds.map(imageId => ({ image_uuid: imageId })),
        );

        for (const registered of registeredImages) {
            const imageId = normalizeImageId(registered.image_uuid);
            pendingAdds.delete(getPendingKey(toolNodeId, imageId));

            if (!imageId) {
                continue;
            }

            if (!nextImageIds.includes(imageId)) {
                await unlinkImageFromDocument(registered.ma_image_node_uuid, context.documentId).catch(error => {
                    console.error('Failed to unlink MA tool image registered after removal:', error);
                });
                delete linkedMap[imageId];
                continue;
            }

            linkedMap[imageId] = registered.ma_image_node_uuid;
        }
    } catch (error) {
        for (const imageId of addedIds) {
            pendingAdds.delete(getPendingKey(toolNodeId, imageId));
        }
        throw error;
    }

    const persisted = buildPersistedMaImageNodeIds(nextImageIds, linkedMap);
    setMemoryLinkedMap(editor, toolNodeId, persisted);
    return persisted;
}

async function runSerializedToolImageSync(
    editor: Editor,
    toolNodeId: string,
    syncFn: () => Promise<ToolImageLinkedMap>,
): Promise<ToolImageLinkedMap> {
    const inFlightSyncByToolNodeId = getInFlightSyncMap(editor);
    const previous = inFlightSyncByToolNodeId.get(toolNodeId);
    const next = (previous ?? Promise.resolve({} as ToolImageLinkedMap))
        .catch(() => ({}) as ToolImageLinkedMap)
        .then(syncFn)
        .finally(() => {
            if (inFlightSyncByToolNodeId.get(toolNodeId) === next) {
                inFlightSyncByToolNodeId.delete(toolNodeId);
            }
        });
    inFlightSyncByToolNodeId.set(toolNodeId, next);
    return next;
}

export async function applyToolXmlUpdatesWithImageSync(
    editor: Editor,
    toolNodeId: string,
    updates: Record<string, unknown>,
    updateAttributes: (attrs: Record<string, unknown>) => void,
): Promise<void> {
    const currentAttrs = findToolNodeAttrsById(editor, toolNodeId);
    if (!currentAttrs) {
        updateAttributes(updates);
        return;
    }

    const previousImageIds = normalizeImageIds(currentAttrs.image_ids);
    const nextImageIds = updates.image_ids !== undefined ? normalizeImageIds(updates.image_ids) : previousImageIds;
    const currentMaImageNodeIds = getMaImageNodeIdsFromAttrs(currentAttrs);

    const imageIdsChanged = updates.image_ids !== undefined && !imageIdSetsEqual(previousImageIds, nextImageIds);
    const hasUnlinkedIds = hasUnlinkedImageIds(editor, toolNodeId, nextImageIds, currentMaImageNodeIds);

    if (!imageIdsChanged && !hasUnlinkedIds) {
        updateAttributes(updates);
        return;
    }

    markPendingAddsForUnlinked(editor, toolNodeId, nextImageIds, currentMaImageNodeIds);

    try {
        const newMaImageNodeIds = await runSerializedToolImageSync(editor, toolNodeId, () =>
            syncToolBuilderImages(editor, {
                toolNodeId,
                nextImageIds,
                currentMaImageNodeIds,
            }),
        );
        updateAttributes({ ...updates, maImageNodeIds: newMaImageNodeIds });
    } catch (error) {
        console.error('Failed to sync tool builder images:', error);
        updateAttributes(updates);
    }
}

export async function ensureToolImagesLinkedForNode(
    editor: Editor,
    toolNodeId: string,
    updateAttributes: (attrs: Record<string, unknown>) => void,
): Promise<void> {
    const currentAttrs = findToolNodeAttrsById(editor, toolNodeId);
    if (!currentAttrs) {
        return;
    }

    const imageIds = normalizeImageIds(currentAttrs.image_ids);
    const currentMaImageNodeIds = getMaImageNodeIdsFromAttrs(currentAttrs);

    if (!hasUnlinkedImageIds(editor, toolNodeId, imageIds, currentMaImageNodeIds)) {
        return;
    }

    markPendingAddsForUnlinked(editor, toolNodeId, imageIds, currentMaImageNodeIds);

    try {
        const newMaImageNodeIds = await runSerializedToolImageSync(editor, toolNodeId, () =>
            syncToolBuilderImages(editor, {
                toolNodeId,
                nextImageIds: imageIds,
                currentMaImageNodeIds,
            }),
        );
        updateAttributes({ maImageNodeIds: newMaImageNodeIds });
    } catch (error) {
        console.error('Failed to ensure tool builder images are linked:', error);
    }
}

export function collectToolNodes(doc: PMNode): Array<{ toolNodeId: string; node: PMNode }> {
    const toolNodes: Array<{ toolNodeId: string; node: PMNode }> = [];
    doc.descendants(node => {
        if (!MA_TOOL_NODE_TYPES.has(node.type.name)) {
            return;
        }
        const toolNodeId = node.attrs?.id;
        if (toolNodeId == null || toolNodeId === '') {
            return;
        }
        toolNodes.push({
            toolNodeId: String(toolNodeId),
            node,
        });
    });
    return toolNodes;
}

async function ensureToolImagesLinkedForNodes(
    editor: Editor,
    toolNodes: Array<{ toolNodeId: string; node: PMNode }>,
    persist: (toolNodeId: string, maImageNodeIds: ToolImageLinkedMap) => void,
): Promise<void> {
    for (const { toolNodeId, node } of toolNodes) {
        const imageIds = normalizeImageIds(node.attrs.image_ids);
        const currentMaImageNodeIds = getMaImageNodeIdsFromAttrs(node.attrs as Record<string, unknown>);

        if (!hasUnlinkedImageIds(editor, toolNodeId, imageIds, currentMaImageNodeIds)) {
            continue;
        }

        markPendingAddsForUnlinked(editor, toolNodeId, imageIds, currentMaImageNodeIds);

        try {
            const newMaImageNodeIds = await runSerializedToolImageSync(editor, toolNodeId, () =>
                syncToolBuilderImages(editor, {
                    toolNodeId,
                    nextImageIds: imageIds,
                    currentMaImageNodeIds,
                }),
            );
            persist(toolNodeId, newMaImageNodeIds);
        } catch (error) {
            console.error('Failed to ensure tool builder images are linked:', error);
        }
    }
}

export async function ensureToolImagesLinkedInDocument(editor: Editor): Promise<void> {
    await ensureToolImagesLinkedForNodes(editor, collectToolNodes(editor.state.doc), (toolNodeId, maImageNodeIds) => {
        persistMaImageNodeIdsOnEditor(editor, toolNodeId, maImageNodeIds);
    });
}

export async function ensureNewlyAddedToolImagesLinked(editor: Editor, oldDoc: PMNode, newDoc: PMNode): Promise<void> {
    const oldToolNodeIds = new Set(collectToolNodes(oldDoc).map(tool => tool.toolNodeId));
    const newlyAddedTools = collectToolNodes(newDoc).filter(tool => !oldToolNodeIds.has(tool.toolNodeId));

    if (newlyAddedTools.length === 0) {
        return;
    }

    await ensureToolImagesLinkedForNodes(editor, newlyAddedTools, (toolNodeId, maImageNodeIds) => {
        persistMaImageNodeIdsOnEditor(editor, toolNodeId, maImageNodeIds);
    });
}

export function clearDeletedToolImageMemory(editor: Editor, oldDoc: PMNode, newDoc: PMNode): void {
    const oldTools = collectToolNodes(oldDoc);
    const newToolNodeIds = new Set(collectToolNodes(newDoc).map(tool => tool.toolNodeId));

    for (const { toolNodeId } of oldTools) {
        if (!newToolNodeIds.has(toolNodeId)) {
            clearMemoryLinkedMap(editor, toolNodeId);
        }
    }
}

export function findDeletedToolMaImageNodeUuids(oldDoc: PMNode, newDoc: PMNode): string[] {
    const oldTools = collectToolNodes(oldDoc);
    const newToolNodeIds = new Set(collectToolNodes(newDoc).map(tool => tool.toolNodeId));
    const uuids: string[] = [];

    for (const { toolNodeId, node } of oldTools) {
        if (newToolNodeIds.has(toolNodeId)) {
            continue;
        }
        for (const maImageNodeUuid of Object.values(getMaImageNodeIdsFromAttrs(node.attrs as Record<string, unknown>))) {
            uuids.push(maImageNodeUuid);
        }
    }

    return uuids;
}
